import React, { useRef, useState } from "react"
import { Theme, Button } from "@pga/pga-component-library"
import {
  Box,
  SubTitle,
  Text,
  Link,
  RequirementsText,
  TooltipTarget,
  StyledTooltip,
  DisabledText,
  Title,
  ApplicationActions,
} from "./styles"
import { withProspectQuery } from "../../../../utils/prospect/withProspectQuery"
import { withPtmApplicationsQuery } from "../../../../utils/prospect/withPtmApplicationsQuery"
import {
  shouldReplaceApplyCtaWithStatus,
  isDeniedPtmApplication,
  isDraftPtmApplication,
  pickLatestApplicationForType,
  PTM_APPLICATION_TYPES,
} from "../../../../utils/prospect/ptmApplicationStatus"
import PtmApplicationStatus from "../../../PtmApplicationStatus"
import calculateRequirementsMet, {
  affiliateRequirementsPassed,
  associateRequirementsPassed,
  isAffiliateRequirementsMet,
  isAssociateRequirementsMet,
} from "../../../../utils/prospect/calculateRequirements"
import { PathBoxLoader } from "../WidgetStates"
import { redirectToMemberApplications } from "../../../../lib/ptmApplicationsRedirect"

export const renderPassedRequirements = requirements =>
  Object.entries(requirements).map(([requirement, isPassed], index) => (
    <div key={index}>
      {isPassed ? requirement : <DisabledText>{requirement}</DisabledText>}
    </div>
  ))

/** @deprecated Prefer PtmApplicationStatus — kept for existing PathBox tests. */
export const ApplicationStatus = ({ status, date }) => (
  <PtmApplicationStatus status={status} date={date} />
)

export const AssociateBox = ({ data, application }) => {
  const [associateTooltipOpen, setAssociateTooltipOpen] = useState(false)
  const passedRequirements = associateRequirementsPassed(data)
  const associateTooltip = useRef(null)
  const replaceApplyCta = shouldReplaceApplyCtaWithStatus(application?.status)
  const showDeniedStatus = isDeniedPtmApplication(application?.status)
  const isResume = isDraftPtmApplication(application?.status)
  const requirementsMet = isAssociateRequirementsMet(data)
  const associateCtaLabel = !requirementsMet
    ? "Awaiting Requirements"
    : isResume
      ? "Resume Associate"
      : "Become Associate"

  return (
    <Box bg>
      <div className="container">
        <SubTitle>
          Associate{" "}
          <span style={{ color: Theme.colors.deepGrey }}>(Recommended)</span>
        </SubTitle>
        <Text>
          To become a PGA Member, you'll need to become a registered Associate
          before completing the PGA Professional Golf Management (PGA PGM)
        </Text>
        <Text>
          <Link
            href="https://www.pga.org/membership/associate-program"
            target="_blank"
            rel="noreferrer"
          >
            Learn More &rarr;
          </Link>
        </Text>
        <RequirementsText>
          Requirements Met: &nbsp;
          <TooltipTarget ref={associateTooltip} id="associateTooltip">
            {calculateRequirementsMet(passedRequirements)}
          </TooltipTarget>
          <StyledTooltip
            isOpen={associateTooltipOpen}
            placement="bottom"
            autohide={false}
            target={associateTooltip}
            toggle={() => setAssociateTooltipOpen(!associateTooltipOpen)}
          >
            {renderPassedRequirements(passedRequirements)}
          </StyledTooltip>
        </RequirementsText>
        {replaceApplyCta ? (
          <PtmApplicationStatus
            status={application.status}
            applicationType={PTM_APPLICATION_TYPES.ASSOCIATE}
          />
        ) : (
          <ApplicationActions>
            {showDeniedStatus ? (
              <PtmApplicationStatus
                status={application.status}
                date={application.updatedAt}
                applicationType={PTM_APPLICATION_TYPES.ASSOCIATE}
              />
            ) : null}
            <Button
              onClick={redirectToMemberApplications}
              disabled={!requirementsMet}
            >
              {associateCtaLabel}
            </Button>
          </ApplicationActions>
        )}
      </div>
    </Box>
  )
}

export const AffiliateBox = ({ data, application }) => {
  const [affiliateTooltipOpen, setAffiliateTooltipOpen] = useState(false)
  const affiliateTooltip = useRef(null)
  const replaceApplyCta = shouldReplaceApplyCtaWithStatus(application?.status)
  const showDeniedStatus = isDeniedPtmApplication(application?.status)
  const isResume = isDraftPtmApplication(application?.status)
  const requirementsMet = isAffiliateRequirementsMet(data)
  const affiliateCtaLabel = !requirementsMet
    ? "Awaiting Requirements"
    : isResume
      ? "Resume Affiliate"
      : "Become Affiliate"

  return (
    <Box>
      <div className="container">
        <SubTitle>Affiliate</SubTitle>
        <Text>
          Want to become a PGA Member, but not yet working in golf? Begin in the
          Affiliate Program, to eventually become an PGA Associate.
        </Text>
        <Text>
          <Link
            href="https://www.pga.org/membership/affiliate-program"
            target="_blank"
            rel="noreferrer"
          >
            Learn More &rarr;
          </Link>
        </Text>
        <RequirementsText>
          Requirements Met: &nbsp;
          <TooltipTarget ref={affiliateTooltip} id="affiliateTooltip">
            {calculateRequirementsMet(affiliateRequirementsPassed(data))}
          </TooltipTarget>
          <StyledTooltip
            isOpen={affiliateTooltipOpen}
            placement="bottom"
            autohide={false}
            target={affiliateTooltip}
            toggle={() => {
              setAffiliateTooltipOpen(!affiliateTooltipOpen)
            }}
          >
            {renderPassedRequirements(affiliateRequirementsPassed(data))}
          </StyledTooltip>
        </RequirementsText>
        {replaceApplyCta ? (
          <PtmApplicationStatus
            status={application.status}
            applicationType={PTM_APPLICATION_TYPES.AFFILIATE}
          />
        ) : (
          <ApplicationActions>
            {showDeniedStatus ? (
              <PtmApplicationStatus
                status={application.status}
                date={application.updatedAt}
                applicationType={PTM_APPLICATION_TYPES.AFFILIATE}
              />
            ) : null}
            <Button
              onClick={redirectToMemberApplications}
              disabled={!requirementsMet}
              outline={requirementsMet}
            >
              {affiliateCtaLabel}
            </Button>
          </ApplicationActions>
        )}
      </div>
    </Box>
  )
}

export const PathStack = ({ data, membershipApplications }) => {
  const associateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.ASSOCIATE,
  )
  const affiliateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.AFFILIATE,
  )

  return (
    <>
      <div className="row">
        <div className="col">
          <AssociateBox data={data} application={associateApplication} />
        </div>
        <div className="col">
          <AffiliateBox data={data} application={affiliateApplication} />
        </div>
      </div>
    </>
  )
}

const PathBox = ({
  currentMember,
  loading,
  membershipApplications,
  ptmApplicationsLoading,
}) => {
  if (loading || ptmApplicationsLoading) return <PathBoxLoader />
  return (
    <div className="container">
      <Title marginTop="20px">Path to Membership</Title>
      <span>
        Welcome to Path to Membership! You must complete all of the below
        requirements in no particular order to apply for the Associate program.
        In order to apply for the Affiliate program, you only need to complete
        the Background Check and Qualifying Level.
      </span>
      <Title marginTop="10px" color={Theme.colors.primary}>
        Choose Your Path
      </Title>
      <PathStack
        data={currentMember}
        membershipApplications={membershipApplications}
      />
    </div>
  )
}

export default withProspectQuery(withPtmApplicationsQuery(PathBox))
